// VideoContainer.tsx
import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from "react";
import {
    // Daily Hooks
    useLocalParticipant,
    useParticipantIds,
    useDaily,
    useDailyEvent,
    // Daily Components
    DailyVideo,
    useLocalSessionId,
    useAudioLevelObserver,
} from "@daily-co/daily-react";
import {
    DailyEventObjectAppMessage,
    // DailyEventObjectParticipantLeft // Keep if using the event data explicitly
} from "@daily-co/daily-js";

// UI Imports
import { Button } from "@component/button";
import {
    Clock as ClockIcon,
    AlertTriangle as AlertTriangleIcon,
    Phone as PhoneIcon,
    Settings as SettingsIcon,
    CheckCircle as CheckCircleIcon,
    Loader2,
    VolumeX,
    InfoIcon,
} from "lucide-react";
import SettingsModal from "@component/interview-settings-modal";
import IssuesDialog from "@component/issues-dialog"; // Add import for IssuesDialog

// Types
import type {
    VideoInterviewStatus,
    TavusInteractionCallbacks,
    DailyAppMessageCallback,
    PipecatAppMessageData,
} from "@type/videoInterview"; // Adjust path as needed
import TranscriptionPanel from "./transcription-panel";
import TranscriptionToggleButton from "./transcription-toggle-button";
import AIPersonaOverlay from "./ai-persona-overlay";
import EnhancedAudioVisualizer from "./enhanced-audio-visualizer";
import FeedbackPopup from "./feedback-popup";
import { catchErrorLog } from "@util/sentry";
import { isVirtualAudioDevice } from "@util/audio-device";
import MicDetectionDialog from "@container/dialog/dialog-mic-detection";
import AudioWarningTooltip from "@component/audio-warning-tooltip";
import LiveStatusBar from "@component/live-status-bar";
// Removed unused import of getBrowserName

// --- Component Props ---
export interface VideoContainerProps {
    status: VideoInterviewStatus;
    errorMessage?: string;
    interviewTitle?: string;
    // onStartInterview: () => void; // Likely handled by parent now
    onEndInterview: () => void; // Callback to signal parent to end the session
    onCloseDialogSuccessRecord: () => void; // Callback when user clicks "Finish" on success screen
    onIssueReport?: () => void; // Optional now as we'll handle it internally
    tavusCallbacks: TavusInteractionCallbacks; // Callbacks for Tavus events
    customGreeting?: string; // Custom greeting message, if any
    id?: string; // Optional ID for the container
    initialContext?: string; // Context seems unused here now, potentially remove
    isInterviewConcluding?: boolean; // Whether the interview is in concluding state
    concludingCountdown?: number; // Countdown seconds until interview ends
    // New props for device management
    currentCameraId?: string;
    currentMicrophoneId?: string;
    currentSpeakerId?: string;
    interviewStartTime: number; // Duration in seconds, used for feedback popup
    userId: string;
    isTestInterview?: boolean; // Add this prop
    conversationId: string;
    assessmentId?: string;
    aiName?: string; // AI interviewer name, defaults to "ourteam AI"
    aiLogoSrc?: string; // AI logo source, uses default if not provided
    interviewLanguage?: string; // New prop for interview language
    isConversationEnded?: boolean; // New prop to indicate if conversation has ended
    onConcluding?: (concluding: boolean) => void; // New callback when concluding state changes
    audioStatus?: 'low' | 'weak' | 'strong' | 'noise';
    connectionStatus?: 'poor' | 'good';
    hasFormAssessment?: boolean;
    hasEligibilityCheck?: boolean;
    tipText?: string;
}

// Add a new type for transcription messages
interface TranscriptionMessage {
    id: string;
    role: string;
    content: string;
    timestamp: Date;
    isComplete: boolean;
}

// Add this helper function before the VideoContainer component
const isYesNoMessage = (content: string): boolean => {
    if (!content) return false;

    // Normalize the content: trim whitespace and remove punctuation
    const normalized = content.trim().replace(/[.,!?;:]*/g, '');

    // Check if it's just YES or NO
    return content === 'YES' || normalized === 'NO';
};

const isContentIncudeInstructions = (content: string): boolean => {
    if (!content) return false;

    if (content.includes("Current Section Instructions:") || content.includes("### MANDATORY QUESTIONS ###")) {
        console.log("text include instructions", content);
        return true;
    }
    return false;
}

// catchErrorLog suppresses any message containing "not found" / "404" / "401" /
// "Unauthorized" (its auth/404 noise filter). Camera failures like NotFoundError
// ("Requested device not found") would otherwise be swallowed, so neutralize those
// substrings for the [camera-switch] diagnostics — we still want them in Sentry.
const sentrySafe = (s: string): string =>
    s
        .replace(/not found/gi, "not-available")
        .replace(/\bunauthorized\b/gi, "unauth")
        .replace(/\b40(1|4)\b/g, (m) => `${m}_`);

export function VideoContainer({
    status,
    errorMessage,
    interviewTitle,
    // onStartInterview, // Removed
    onEndInterview,
    onCloseDialogSuccessRecord,
    onIssueReport, // Keep for backward compatibility
    tavusCallbacks,
    isInterviewConcluding = false,
    concludingCountdown = 0,
    customGreeting = "",
    // Destructure new props
    currentCameraId,
    currentMicrophoneId,
    currentSpeakerId, interviewStartTime,
    userId,
    isTestInterview = false,
    conversationId,
    assessmentId,
    aiName = "ourteam AI", // Default value for backward compatibility
    aiLogoSrc,
    interviewLanguage, // Custom logo source
    isConversationEnded = false,
    onConcluding = () => { },
    audioStatus = "low",
    connectionStatus = "poor",
    hasFormAssessment = false,
    hasEligibilityCheck = false,
    tipText,
}: VideoContainerProps): React.ReactElement | null {
    // --- Daily Hooks ---
    const callObject = useDaily();
    const localParticipant = useLocalParticipant();
    const remoteParticipantIds = useParticipantIds({ filter: "remote" });

    // FirstMove color scheme detection
    const isFirstMoveInterview = aiName?.toLowerCase().includes('first move') || false;
    const primaryColorClasses = isFirstMoveInterview
        ? {
            bg: 'bg-black',
            bgHover: 'hover:bg-gray-800',
            bgLight: 'bg-gray-50',
            bgLightDark: 'dark:bg-gray-900/30',
            text: 'text-gray-600',
            textDark: 'dark:text-gray-400',
            loader: 'text-gray-500'
        }
        : {
            bg: 'bg-blue-600',
            bgHover: 'hover:bg-blue-700',
            bgLight: 'bg-blue-50',
            bgLightDark: 'dark:bg-blue-900/30',
            text: 'text-blue-800',
            textDark: 'dark:text-blue-300',
            loader: 'text-blue-500'
        };

    // -- Issues Dialog State --
    const [isIssuesDialogOpen, setIsIssuesDialogOpen] = useState<boolean>(false);

    const [isTranscriptionOpen, setIsTranscriptionOpen] = useState<boolean>(true); // Assuming this is used for toggling transcription panel

    // Assuming the first remote participant is the AI
    const aiParticipantId = useMemo(
        () => (remoteParticipantIds.length > 0 ? remoteParticipantIds[0] : null),
        [remoteParticipantIds]
    );

    // --- UI State ---
    // Removed showIssueModal, assuming onIssueReport triggers modal in parent
    // Removed showConfirmationModal, handled by parent
    const [isMaximized, setIsMaximized] = useState<boolean>(true); // Start maximized
    // Removed showChat, showParticipants as they are not implemented
    const [showSettings, setShowSettings] = useState<boolean>(false);
    const [elapsedTime, setElapsedTime] = useState<number>(0); // Time in seconds
    const [showEndingSoonWarning, setShowEndingSoonWarning] = useState<boolean>(false);

    const [transcriptionMessages, setTranscriptionMessages] = useState<TranscriptionMessage[]>([]);
    const [isAiSpeaking, setIsAiSpeaking] = useState<boolean>(false);
    const [isUserSpeaking, setIsUserSpeaking] = useState<boolean>(false); // Assuming this is used for user speaking state

    // AI thinking state management
    const [isAiThinking, setIsAiThinking] = useState<boolean>(false);
    const [canStartThinking, setCanStartThinking] = useState<boolean>(false);
    const [userWasSpeaking, setUserWasSpeaking] = useState<boolean>(false);
    const [showMicDialog, setShowMicDialog] = useState<boolean>(false);

    // In-interview audio warning tooltip (ticket #1870 + #1871, PRD §3.2).
    //
    // "isDeviceWarning" — mic is dead or very weak. Shows the yellow "!" badge
    // on the Settings icon AND lights up the Microphone label inside the modal,
    // because the fix requires adjusting device settings.
    //
    // "hasAnyWarning" — any audio issue worth showing a tooltip for (device or
    // environmental). Noise is environmental so it does NOT light up the badge.
    //
    // Re-arm: reset the dismissed flag whenever a *new* warning event begins.
    // That's either (a) audio recovering to "strong" and then degrading again,
    // or (b) the warning type itself changing (e.g. weak → noise) without a
    // strong checkpoint in between — the second scenario is still a distinct
    // new event worth showing the user.
    const [isAudioWarningDismissed, setIsAudioWarningDismissed] = useState<boolean>(false);
    const prevAudioStatusRef = useRef<typeof audioStatus>(audioStatus);
    useEffect(() => {
        const prev = prevAudioStatusRef.current;
        const recoveredAndDegraded = prev === "strong" && audioStatus !== "strong";
        const switchedWarningType =
            prev !== "strong" && audioStatus !== "strong" && prev !== audioStatus;
        if (recoveredAndDegraded || switchedWarningType) {
            setIsAudioWarningDismissed(false);
        }
        prevAudioStatusRef.current = audioStatus;
    }, [audioStatus]);
    const isDeviceWarning = audioStatus === "low" || audioStatus === "weak";
    const hasAnyWarning = isDeviceWarning || audioStatus === "noise";
    const showAudioWarning = hasAnyWarning && !isAudioWarningDismissed;

    // Enhanced speech gap handling
    const thinkingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const THINKING_DELAY = 1500; // 1.5 seconds delay before thinking starts
    const [selectedAudioDevice, setSelectedAudioDevice] = useState<string | null>(currentMicrophoneId || null);
    const [selectedVideoDevice, setSelectedVideoDevice] = useState<string | null>(currentCameraId || null);
    const [showTooltip, setShowTooltip] = useState<boolean>(true);

    // Virtual mic detection (e.g. LoomAudioDevice, BlackHole, OBS Virtual
    // Audio). These devices produce silence as a capture source, but the
    // SpeechDetectionContext above runs on the page's *default* mic so the
    // existing `isDeviceWarning` flag misses them entirely. Resolve the
    // selected mic's label via enumerateDevices and flag the warning
    // independently. Re-run when the user switches device mid-session.
    const [isVirtualMicSelected, setIsVirtualMicSelected] = useState<boolean>(false);
    useEffect(() => {
        let cancelled = false;
        const resolve = async () => {
            if (!selectedAudioDevice) {
                if (!cancelled) setIsVirtualMicSelected(false);
                return;
            }
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const match = devices.find(
                    (d) => d.kind === "audioinput" && d.deviceId === selectedAudioDevice,
                );
                if (cancelled) return;
                setIsVirtualMicSelected(isVirtualAudioDevice(match?.label));
            } catch (err) {
                catchErrorLog(`VideoContainer: failed to resolve mic label: ${err}`);
                if (!cancelled) setIsVirtualMicSelected(false);
            }
        };
        resolve();
        return () => {
            cancelled = true;
        };
    }, [selectedAudioDevice]);

    // Either trigger lights up the settings-button badge and the Microphone
    // label warning inside the device settings modal.
    const shouldShowMicWarning = isDeviceWarning || isVirtualMicSelected;

    const localSessionId = useLocalSessionId();
    const lastProcessedTime = useRef(0);

    useAudioLevelObserver(
        localSessionId,
        useCallback(() => {
            const now = Date.now();
            // Throttle processing to once every 2 seconds
            if (now - lastProcessedTime.current >= 2000) {
                lastProcessedTime.current = now;
            }
        }, [])
    )

    // --- Refs ---
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const prevStatusRef = useRef<VideoInterviewStatus | null>(null);

    useEffect(() => {
        async function checkAudioTrack() {
            try {
                await navigator.mediaDevices.getUserMedia({ audio: true });
                setShowMicDialog(false);
            } catch (error) {
                console.error("Error accessing microphone:", error);
                setShowMicDialog(true);
            }
        }

        checkAudioTrack();
    }, [])

    // Helper function to clear thinking timeout
    const clearThinkingTimeout = useCallback(() => {
        if (thinkingTimeoutRef.current) {
            clearTimeout(thinkingTimeoutRef.current);
            thinkingTimeoutRef.current = null;
        }
    }, []);

    // Helper function to start thinking with delay
    const startThinkingWithDelay = useCallback(() => {
        clearThinkingTimeout();
        thinkingTimeoutRef.current = setTimeout(() => {
            setIsAiThinking(true);
            thinkingTimeoutRef.current = null;
        }, THINKING_DELAY);
    }, [clearThinkingTimeout, THINKING_DELAY]);

    // --- Callbacks ---
    const handleEndCall = useCallback(() => {
        // No need to manage isMaximized state here, just call parent's end function
        onEndInterview();
    }, [onEndInterview]);

    const handleOpenSettings = () => {
        setShowSettings(true);
    };

    const handleCloseSettings = () => {
        setShowSettings(false);
    };

    const handleSettingsSave = async (devices: { camera?: string; microphone?: string; speaker?: string }) => {
        if (!callObject) {
            console.error("Daily call object not available to save settings.");
            handleCloseSettings();
            return;
        }

        const newCameraId = devices.camera;
        const newMicrophoneId = devices.microphone;
        const newSpeakerId = devices.speaker;

        // Honour the device the modal returned (the currentCamera/Microphone PROPS are
        // stale on Android). Decide "changed" against the live local selection.
        const audioDeviceToSet = newMicrophoneId ?? currentMicrophoneId;
        const videoDeviceToSet = newCameraId ?? currentCameraId;
        const cameraChanged = !!newCameraId && newCameraId !== selectedVideoDevice;
        const micChanged = !!newMicrophoneId && newMicrophoneId !== selectedAudioDevice;

        try {
            catchErrorLog(`[camera-switch] requesting videoDeviceId=${cameraChanged ? videoDeviceToSet : "(unchanged)"} audioDeviceId=${micChanged ? audioDeviceToSet : "(unchanged)"} cameraChanged=${cameraChanged} micChanged=${micChanged} prevSelected=${selectedVideoDevice ?? "?"}`);

            // Switch the CAMERA on its OWN call (video-only). The Android logs showed the
            // SAME deviceId arriving as BOTH videoDeviceId AND audioDeviceId — asking
            // Daily to open one camera as mic+camera makes the video source fail
            // (NotReadableError) and Daily then silently falls back to another camera.
            // So NEVER carry an audio id on a camera switch (and vice-versa). Retry on
            // NotReadableError AND on a silent fall-back (Daily can resolve yet apply a
            // different camera when the selected one fails to start).
            if (cameraChanged) {
                // Preserve the user's camera on/off intent — capture whether video was
                // already OFF before the switch. A user who intentionally muted their
                // camera must NOT be re-published on video just for changing devices.
                const wasVideoOff = !!callObject.participants()?.local?.tracks?.video?.off;
                let cameraStarted = false;
                // We turn video OFF to free the Android sensor before re-acquiring; track
                // that so we can RESTORE it on ANY failed switch and never strand a user
                // who had video on in a muted state (Jack review). The `finally` below
                // restores it whether the loop succeeds, exhausts, or throws.
                let temporarilyDisabledVideo = false;
                try {
                    for (let attempt = 0; attempt < 3; attempt++) {
                        // Release the currently-held camera FIRST so Android frees the
                        // sensor before we acquire the target one. The active camera is
                        // otherwise never released, so re-acquiring a different camera fails
                        // with NotReadableError ("Could not start video source") and Daily
                        // falls back. Only when video was ON, so a muted user's state is
                        // untouched.
                        if (!wasVideoOff) {
                            try { callObject.setLocalVideo(false); temporarilyDisabledVideo = true; } catch (offErr) { catchErrorLog(`[camera-switch] setLocalVideo(false) before switch failed: ${offErr}`); }
                            await new Promise((resolve) => setTimeout(resolve, 600));
                        }
                        try {
                            await callObject.setInputDevicesAsync({ videoDeviceId: videoDeviceToSet || undefined });
                        } catch (switchErr) {
                            const errLabel = sentrySafe(switchErr instanceof Error ? `${switchErr.name}: ${switchErr.message}` : String(switchErr));
                            catchErrorLog(`[camera-switch] setInputDevicesAsync(video) FAILED (attempt ${attempt + 1}) videoDeviceId=${videoDeviceToSet}: ${errLabel}`);
                            if (switchErr instanceof Error && switchErr.name === "NotReadableError" && attempt < 2) {
                                await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
                                continue;
                            }
                            throw switchErr;
                        }
                        // Re-enable and WAIT for the track to actually come ON. Android can
                        // leave the local video off (state=off / {byUser:true}) even after
                        // the deviceId applies, so retry the enable until it's on. A muted
                        // user is left off (their intent is preserved).
                        let trackOn = wasVideoOff;
                        for (let en = 0; en < 3 && !trackOn; en++) {
                            callObject.setLocalVideo(true);
                            await new Promise((resolve) => setTimeout(resolve, 350));
                            const t = callObject.participants()?.local?.tracks?.video;
                            trackOn = !!t && t.state !== "off";
                            if (!trackOn) catchErrorLog(`[camera-switch] video still off after enable try ${en + 1} (off=${JSON.stringify(t?.off ?? null)})`);
                        }
                        let appliedId: string | undefined;
                        try {
                            const applied = await callObject.getInputDevices();
                            appliedId = (applied?.camera as Partial<MediaDeviceInfo> | undefined)?.deviceId;
                        } catch { /* probe is best-effort */ }
                        const deviceOk = !videoDeviceToSet || !appliedId || appliedId === videoDeviceToSet;
                        // Success only when the camera applied AND the track is actually ON
                        // — "deviceId matches but track off" is still a black screen.
                        if (deviceOk && trackOn) {
                            cameraStarted = true;
                            temporarilyDisabledVideo = false; // video is back on
                            catchErrorLog(`[camera-switch] applied OK + ON: deviceId=${appliedId ?? "?"} (attempt ${attempt + 1})`);
                            break;
                        }
                        catchErrorLog(`[camera-switch] retry needed: deviceOk=${deviceOk} trackOn=${trackOn} applied=${appliedId} requested=${videoDeviceToSet} (attempt ${attempt + 1})`);
                        if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
                    }

                    // Last resort — if the chosen camera never produced a live track (Android
                    // refusing to (re)start that specific deviceId via setInputDevicesAsync),
                    // fall back to Daily's native cycleCamera(), which handles the mobile
                    // release/acquire internally. It may land on a different camera than the
                    // exact one picked, but a working camera beats a black screen.
                    if (!cameraStarted && !wasVideoOff) {
                        try {
                            catchErrorLog(`[camera-switch] specific switch failed — falling back to cycleCamera()`);
                            const cycled = await callObject.cycleCamera();
                            callObject.setLocalVideo(true);
                            await new Promise((resolve) => setTimeout(resolve, 350));
                            const t = callObject.participants()?.local?.tracks?.video;
                            if (t && t.state !== "off") temporarilyDisabledVideo = false;
                            catchErrorLog(`[camera-switch] cycleCamera() → device=${JSON.stringify(cycled?.device ?? null)} trackState=${t?.state ?? "?"}`);
                        } catch (cycleErr) {
                            catchErrorLog(`[camera-switch] cycleCamera() fallback failed: ${sentrySafe(cycleErr instanceof Error ? `${cycleErr.name}: ${cycleErr.message}` : String(cycleErr))}`);
                        }
                    }
                } finally {
                    // Never strand a user who had video ON in a muted state: if we disabled
                    // it and it's still off (every attempt + the cycleCamera fallback
                    // failed, or the switch threw), restore it best-effort.
                    if (!wasVideoOff && temporarilyDisabledVideo) {
                        try { callObject.setLocalVideo(true); } catch (restoreErr) { catchErrorLog(`[camera-switch] failed to restore local video after switch: ${restoreErr}`); }
                    }
                }
            }

            // Switch the MIC on its OWN call (audio-only) when it actually changed.
            if (micChanged) {
                try {
                    await callObject.setInputDevicesAsync({ audioDeviceId: audioDeviceToSet || undefined });
                } catch (micErr) {
                    catchErrorLog(`[camera-switch] mic switch failed audioDeviceId=${audioDeviceToSet}: ${sentrySafe(micErr instanceof Error ? `${micErr.name}: ${micErr.message}` : String(micErr))}`);
                }
            }

            // Final track-state diagnostic.
            if (cameraChanged) {
                const localVideo = callObject.participants()?.local?.tracks?.video;
                if (localVideo) {
                    catchErrorLog(`[camera-switch] local video track: state=${localVideo.state} off=${JSON.stringify(localVideo.off ?? null)} blocked=${JSON.stringify(localVideo.blocked ?? null)}`);
                }
            }

            setSelectedAudioDevice(audioDeviceToSet || "default");
            setSelectedVideoDevice(videoDeviceToSet || "default");
            console.log("Input devices updated successfully in Daily.");
            console.log("New input devices:", { audioDeviceId: audioDeviceToSet, videoDeviceId: videoDeviceToSet });
        } catch (error) {
            const errLabel = sentrySafe(error instanceof Error ? `${error.name}: ${error.message}` : String(error));
            catchErrorLog(`[camera-switch] device update failed videoDeviceId=${videoDeviceToSet} audioDeviceId=${audioDeviceToSet}: ${errLabel}`);
            // Intentionally do NOT force the camera back on here — we no longer turn it
            // off ourselves, and re-enabling could re-publish a user who had video muted.
        }

        if (newSpeakerId !== undefined && newSpeakerId !== currentSpeakerId) {
            try {
                console.log("Attempting to update output device:", { outputDeviceId: newSpeakerId });
                await callObject.setOutputDeviceAsync({ outputDeviceId: newSpeakerId });
                console.log("Output device updated successfully in Daily.");
            } catch (error) {
                console.error("Error updating output device:", error);
                // Optionally, show an error message to the user
            }
        }
        handleCloseSettings(); // Close modal after attempting to save
    };

    // -- Issues Dialog Handlers --
    const handleOpenIssuesDialog = useCallback(() => {
        setIsIssuesDialogOpen(true);
    }, []);

    const handleIssuesDialogClose = useCallback(() => {
        setIsIssuesDialogOpen(false);
    }, []);

    const handleIssueSubmit = useCallback((issueType: string, details: string) => {
        console.log(`Issue reported: ${issueType}`, details);
        // If parent provided an onIssueReport callback, still call it for backward compatibility
        if (onIssueReport) {
            onIssueReport();
        }
    }, [onIssueReport]);

    // --- Daily Event Handling ---
    const handleAppMessage: DailyAppMessageCallback = (event: DailyEventObjectAppMessage) => {
        const pipecat_event = event.data as PipecatAppMessageData
        // console.log("Event received:", pipecat_event);

        if (pipecat_event.type === "user-started-speaking") {
            // User started speaking
            setIsUserSpeaking(true);

            // Clear any pending thinking timeout since user is speaking again
            clearThinkingTimeout();

            // Stop thinking if already thinking (user resumed speaking)
            if (isAiThinking) {
                setIsAiThinking(false);
            }

            // If AI can start thinking and user starts speaking, mark that user was speaking
            if (canStartThinking) {
                setUserWasSpeaking(true);
            }
        }
        else if (pipecat_event.type === "user-stopped-speaking") {
            // User stopped speaking
            setIsUserSpeaking(false);

            // Finalize any in-flight user transcription messages
            setTranscriptionMessages((prevMessages) =>
                prevMessages.map((msg) =>
                    msg.role === "user" && !msg.isComplete
                        ? { ...msg, isComplete: true }
                        : msg
                )
            );

            // If AI can start thinking and user was speaking, start thinking with delay
            // This accounts for potential speech gaps - we wait 1.5 seconds to ensure user is truly done
            if (canStartThinking && userWasSpeaking) {
                startThinkingWithDelay();
            }
        }
        else if (pipecat_event.type === "user-transcription") {
            // User speech-to-text update from Pipecat
            const rawText = pipecat_event.data?.text;
            const isFinal = (pipecat_event.data as { final?: boolean })?.final ?? true;

            if (typeof rawText !== "string") return;
            const userText = rawText.replace(/\n/g, "").trim();
            if (!userText || isYesNoMessage(userText)) return;

            setTranscriptionMessages((prevMessages) => {
                // Find the last in-flight user message (interim transcription update)
                let lastUserIdx = -1;
                for (let i = prevMessages.length - 1; i >= 0; i--) {
                    if (prevMessages[i].role === "user" && !prevMessages[i].isComplete) {
                        lastUserIdx = i;
                        break;
                    }
                }

                if (lastUserIdx !== -1) {
                    // Replace the interim content with the latest cumulative transcript
                    return prevMessages.map((msg, i) =>
                        i === lastUserIdx
                            ? { ...msg, content: userText, isComplete: isFinal }
                            : msg
                    );
                }

                // No in-flight user message — create a new one
                const newMessage: TranscriptionMessage = {
                    id: `user-${Date.now()}`,
                    role: "user",
                    content: userText,
                    timestamp: new Date(),
                    isComplete: isFinal,
                };
                return [...prevMessages, newMessage];
            });
        }
        else if (pipecat_event.type === "bot-tts-started") {
            // Bot is streaming text
            setIsAiSpeaking(true);

            // Stop AI thinking when bot starts speaking and clear any pending timeouts
            clearThinkingTimeout();
            setIsAiThinking(false);
            setCanStartThinking(false);
            setUserWasSpeaking(false);
        }
        else if (pipecat_event.type === "bot-tts-text") {
            // Bot is streaming text
            // add a delay
            if (!isUserSpeaking) {
                let streamingText = pipecat_event.data?.text;
                console.log('Pipecat bot-tts-text event data:', streamingText);
                // remove trailing newline and trim spaces
                if (streamingText) {
                    streamingText = streamingText.replace(/\n/g, "").trim();
                }

                if (isContentIncudeInstructions(streamingText)) {
                    return;
                }

                // Skip if it's a welcome greeting
                if (pipecat_event.data?.is_welcome_greeting) {
                    return;
                }

                // Skip if it's a YES/NO message
                if (typeof streamingText === 'string' && !isYesNoMessage(streamingText)) {
                    setTranscriptionMessages((prevMessages) => {
                        // Check if we have an incomplete message to update
                        const hasIncompleteMessage = prevMessages.some(msg => !msg.isComplete && msg.role === 'ai');

                        if (hasIncompleteMessage) {
                            // Append to the existing incomplete message instead of replacing the content
                            return prevMessages.map(msg =>
                                (!msg.isComplete && msg.role === 'ai')
                                    ? { ...msg, isComplete: false, content: msg.content + " " + streamingText }
                                    : msg
                            );
                        } else {
                            // Create a new message if there's no incomplete one
                            const newMessage: TranscriptionMessage = {
                                id: Date.now().toString(),
                                role: 'ai',
                                content: streamingText,
                                timestamp: new Date(),
                                isComplete: false
                            };
                            return [...prevMessages, newMessage];
                        }
                    });

                    if (isConversationEnded && !isInterviewConcluding) {
                        setTimeout(() => {
                            console.log(`Interview is concluding based on conversation ended state. isConversationEnded ${isConversationEnded} | concluding: ${isInterviewConcluding}}`);
                            onConcluding(true);
                        }, 3000);
                    }
                }
            }
        }
        else if (pipecat_event.type === "bot-stopped-speaking") {
            // Bot has finished speaking
            setIsAiSpeaking(false);

            // Clear any existing thinking timeouts and reset state
            clearThinkingTimeout();
            setIsAiThinking(false);

            // Enable the possibility for AI to start thinking
            setCanStartThinking(true);
            setUserWasSpeaking(false);
            // Mark the current streaming message as complete
            setTranscriptionMessages((prevMessages) => {
                // Only update if the message isn't a YES/NO message
                const message = prevMessages.find(msg => !msg.isComplete && msg.role === 'ai');
                if (message && isYesNoMessage(message.content)) {
                    // Filter out YES/NO messages entirely
                    return prevMessages.filter(msg => msg.id !== message.id);
                }

                return prevMessages.map(msg =>
                    (!msg.isComplete && msg.role === 'ai')
                        ? { ...msg, isComplete: true }
                        : msg
                );
            });
        }
    }
    useDailyEvent("app-message", handleAppMessage);

    // --- Effects ---

    // Push the INITIAL device selection into Daily ONCE, when the call object and a
    // selected device first become available. Subsequent user switches go through
    // handleSettingsSave only. Previously this effect re-ran on every
    // selectedVideo/AudioDevice change, so clicking "Apply Settings" fired a SECOND,
    // redundant setInputDevicesAsync right after handleSettingsSave's — restarting the
    // just-acquired camera. On Android the camera is released asynchronously, so that
    // back-to-back restart left the device busy and the video went black.
    const didInitDevicesRef = useRef(false);
    useEffect(() => {
        if (didInitDevicesRef.current) return;
        // Set initial audio/video device states
        if (callObject && (selectedVideoDevice || selectedAudioDevice)) {
            didInitDevicesRef.current = true;
            callObject.setInputDevicesAsync({
                audioDeviceId: selectedAudioDevice || undefined,
                videoDeviceId: selectedVideoDevice || undefined,
            }).catch((error) => {
                catchErrorLog(`Error setting input devices: ${error}`);
            });
        }
    }, [selectedVideoDevice, selectedAudioDevice, callObject]);

    // Timer effect
    useEffect(() => {
        // Only start the timer if the status is 'active' and there isn't one running
        if (status === "active" && !timerRef.current) {
            // Reset timer state only when transitioning TO active
            const isTransitioningToActive = prevStatusRef.current !== "active";
            if (isTransitioningToActive) {
                setElapsedTime(0);
                setShowEndingSoonWarning(false);
            }

            timerRef.current = setInterval(() => {
                setElapsedTime((prev) => {
                    const newTime = prev + 1;
                    const WARNING_TIME = 20 * 60; // 20 minutes in seconds

                    if (newTime === WARNING_TIME) {
                        setShowEndingSoonWarning(true);
                    }

                    return newTime;
                });
            }, 1000);
        }

        // Update previous status ref *after* checking transition
        prevStatusRef.current = status;

        // Cleanup: Clear interval only if status is NOT active OR on unmount
        return () => {
            if (status !== 'active' && timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
                // Reset timer state if transitioning away from active
                setElapsedTime(0);
                setShowEndingSoonWarning(false);
            }
            // Ensure cleanup on unmount regardless of status
            if (!status && timerRef.current) { // Check if status becomes undefined/null during unmount potentially
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            // Clean up thinking timeout
            clearThinkingTimeout();
        };
        // Add handleEndCall to dependencies as it's used inside the interval
    }, [status, handleEndCall, clearThinkingTimeout]);

    // Effect to auto-hide the warning popup
    useEffect(() => {
        let hideTimer: NodeJS.Timeout | null = null;
        if (showEndingSoonWarning) {
            hideTimer = setTimeout(() => {
                setShowEndingSoonWarning(false);
            }, 30000); // Hide after 30 seconds
        }
        return () => {
            if (hideTimer) clearTimeout(hideTimer);
        };
    }, [showEndingSoonWarning]);

    // Effect to set maximized state based on status
    useEffect(() => {
        // Automatically maximize when entering active state, minimize otherwise
        if (status === "active") {
            setIsMaximized(true);
        } else {
            setIsMaximized(false); // Minimize if connecting, completed, error etc.
        }
    }, [status]);

    // --- Derived State ---
    const formattedTime = useMemo(() => {
        const minutes = Math.floor(elapsedTime / 60);
        const seconds = elapsedTime % 60;
        return `${minutes.toString().padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}`;
    }, [elapsedTime]);

    const isTimerExceedingLimit = elapsedTime > 20 * 60; // Example: highlight after 20 mins

    const isLocalMuted = localParticipant ? !localParticipant.audio : true; // Default to muted if no participant object


    // ** Connecting State **
    if (status === "connecting") {
        return (
            <div className="w-full h-full min-h-[480px] flex flex-col items-center justify-center text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-900">
                <Loader2 className={`h-10 w-10 animate-spin mb-4 ${primaryColorClasses.loader}`} />
                Connecting to Interview...
                {errorMessage && (
                    <p className="mt-2 text-red-500 text-sm">Error: {errorMessage}</p>
                )}
            </div>
        );
    }


    // ** Active / Ending Video Call UI **
    // Show this UI if status is 'active' or 'ending' (ending shows same UI until transition to completed)
    if (status === "active") {
        return (
            <div
                className={`${isMaximized ? "fixed inset-0 z-50 bg-black" : "relative w-full max-w-4xl mx-auto my-4" // Added margin when not maximized
                    }`}
                id={conversationId || "ai-interview-active"}
            >
                {/* Container */}
                <div
                    id="ai-interview-container"
                    className={`w-full h-full bg-black ${isMaximized ? '' : 'rounded-lg shadow-xl'} overflow-hidden flex flex-col min-h-[480px]`} // Conditional rounding/shadow
                >
                    {/* Concluding Banner - Only shown when isInterviewConcluding is true */}
                    {isInterviewConcluding && (
                        <div className="py-2 px-3 sm:px-4 flex items-center justify-between gap-2 text-xs sm:text-base" style={{ backgroundColor: '#2563eb', color: '#fafafa' }}>
                            <div className="flex items-center gap-2 min-w-0">
                                <ClockIcon className="h-4 w-4 flex-shrink-0" />
                                <span className="font-medium truncate">Interview concluded</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="hidden sm:inline">Room will automatically close in:</span>
                                <span className="sm:hidden">Closes in:</span>
                                <span className="font-bold text-lg sm:text-xl">{concludingCountdown}</span>
                            </div>
                        </div>
                    )}

                    {/* Main Content Area - Restructured as a row */}
                    <div className="flex-1 flex flex-row min-h-0 relative">
                        {/* Left column: Video + Controls */}
                        <div className="flex flex-col flex-1">
                            {/* Main Video Area - Shows LOCAL participant */}
                            <div className="relative flex-1 overflow-hidden rounded-lg">
                                <div className="absolute inset-0">
                                    {localParticipant ? (
                                        <>
                                            <DailyVideo
                                                // Re-key on the selected camera so the
                                                // element REMOUNTS and freshly attaches the
                                                // new track on a device switch. Without this,
                                                // Android can keep showing the old (now-ended)
                                                // track's last frame → a black video.
                                                key={`${localParticipant.session_id}:${selectedVideoDevice ?? ""}`}
                                                sessionId={localParticipant.session_id}
                                                type="video"
                                                className="w-full h-full object-cover"
                                                automirror // Mirror local video
                                                // Conditionally render based on video state
                                                style={{ display: localParticipant.video ? "block" : "none" }}
                                            />
                                            {/* Show Avatar if video is off */}
                                            {!localParticipant.video && (
                                                <div className="w-full h-full flex items-center justify-center bg-gray-700">
                                                    <div className="h-24 w-24 rounded-full bg-gray-500 flex items-center justify-center text-white text-4xl font-bold">
                                                        {/* Use first letter of username or 'Y' as fallback */}
                                                        {localParticipant.user_name
                                                            ?.substring(0, 1)
                                                            ?.toUpperCase() || "Y"}
                                                    </div>
                                                </div>
                                            )}
                                            {/* Muted Icon */}
                                            {isLocalMuted && (
                                                <div className="absolute top-2 right-2 bg-red-500/80 p-1.5 rounded-full">
                                                    <VolumeX className="h-4 w-4 text-white" />
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        // Loading state if local participant data isn't available yet
                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading
                                            your video...
                                        </div>
                                    )}
                                </div>

                                {/* Mobile TIP - floating at top of video frame */}
                                <div className="sm:hidden absolute top-2 left-2 right-2 flex items-center gap-2 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 z-10">
                                    <span className="text-blue-400 bg-blue-500/20 rounded px-1.5 py-0.5 flex-shrink-0 whitespace-nowrap text-[10px] font-semibold">
                                        TIP
                                    </span>
                                    <span className="text-[11px] text-gray-300 leading-snug">
                                        {tipText ?? "Aim for 30 to 60 seconds per answer."}
                                    </span>
                                </div>

                                {/* Live Status Bar */}
                                {!isInterviewConcluding && (
                                    <LiveStatusBar
                                        state={
                                            isAiSpeaking ? "ai-speaking" :
                                                isAiThinking ? "ai-thinking" :
                                                    (isUserSpeaking || canStartThinking) ? "user-turn" :
                                                        null
                                        }
                                        aiName={aiName}
                                    />
                                )}

                                {/* "You" label with audio visualizer - positioned above the bottom bar */}
                                <div className="absolute bottom-2 left-2">
                                    <div
                                        className={`text-sm font-medium text-white bg-black/60 px-3 py-1.5 rounded-lg flex flex-row items-center ${isUserSpeaking ? "gap-2" : ""}`}
                                    >
                                        You
                                        {isUserSpeaking && (
                                            <EnhancedAudioVisualizer
                                                isActive={isUserSpeaking}
                                                variant="bars"
                                                color="green"
                                            />
                                        )}
                                    </div>
                                </div>                                {/* AI Participant Video as PiP */}
                                {aiParticipantId && (
                                    <AIPersonaOverlay
                                        name={aiName}
                                        imageSrc={""}
                                        isSpeaking={isAiSpeaking}
                                        className="absolute bottom-4 right-4 w-32 h-24 md:w-48 md:h-36"
                                        logoSrc={aiLogoSrc}
                                        primaryColor={isFirstMoveInterview ? 'black' : 'blue'}
                                    />
                                )}
                            </div>

                            {/* Controls Footer */}
                            <div className="bg-gray-900 text-white p-3 flex justify-between items-center flex-shrink-0">
                                {/* Left Side - Timer & Warning */}
                                <div className="flex-1 flex items-center relative gap-2">
                                    <div className="flex flex-row w-full items-center justify-start gap-4">
                                        {/* Timer */}
                                        <span className={`text-sm font-mono ${isTimerExceedingLimit ? 'text-red-400' : 'text-gray-300'} flex items-center`}>
                                            <ClockIcon className="h-3.5 w-3.5 inline mr-1.5 flex-shrink-0" />
                                            {formattedTime}
                                        </span>
                                        {/* horizontal separator */}
                                        {interviewTitle && (
                                            <>
                                                <div className="border-l border-gray-600 h-5" />
                                                <div className="w-[100px] sm:min-w-[80%] truncate" title={interviewTitle}>
                                                    {interviewTitle}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {/* Warning Popup */}
                                    {showEndingSoonWarning && (
                                        <div className="absolute bottom-full left-0 mb-2 bg-yellow-500 text-black text-xs py-1 px-2 rounded shadow-lg whitespace-nowrap z-20 animate-pulse">
                                            <AlertTriangleIcon className="h-3 w-3 inline mr-1" /> Ending Soon
                                        </div>
                                    )}
                                </div>

                                {/* Center Controls */}
                                <div className="flex-1 flex items-center justify-center gap-2 md:gap-3">
                                    {/* Settings */}
                                    <div className="relative">
                                        {showAudioWarning && (
                                            <AudioWarningTooltip
                                                variant={
                                                    audioStatus === "low"
                                                        ? "low"
                                                        : audioStatus === "noise"
                                                        ? "noise"
                                                        : "weak"
                                                }
                                                onClose={() => setIsAudioWarningDismissed(true)}
                                                {...(isDeviceWarning
                                                    ? {
                                                          onOpenSettings: () => {
                                                              setIsAudioWarningDismissed(true);
                                                              handleOpenSettings();
                                                          },
                                                      }
                                                    : {})}
                                            />
                                        )}
                                        <button
                                            onClick={handleOpenSettings}
                                            title={
                                                isVirtualMicSelected
                                                    ? "Virtual microphone selected — open Settings to switch"
                                                    : "Settings"
                                            }
                                            className={`p-2 md:p-3 rounded-full transition-colors bg-gray-700 hover:bg-gray-600 relative`}
                                        >
                                            <SettingsIcon className="h-5 w-5" />
                                            {shouldShowMicWarning && (
                                                <div className="absolute -top-1 -right-1 h-4 w-4 bg-yellow-500 rounded-full flex items-center justify-center ring-2 ring-gray-700">
                                                    <span className="text-[10px] font-bold text-gray-900 leading-none">!</span>
                                                </div>
                                            )}
                                        </button>
                                    </div>
                                    {/* End Call */}
                                    <button onClick={handleEndCall} title="End Interview" className="p-2 md:p-3 rounded-full bg-red-500 hover:bg-red-600">
                                        <PhoneIcon className="h-5 w-5" />
                                    </button>
                                    {/* Report Issue - Updated to open dialog directly */}
                                    <button onClick={handleOpenIssuesDialog} title={"Report Issue"} className={`p-2 md:p-3 rounded-full transition-colors bg-gray-700 hover:bg-gray-600`}>
                                        <AlertTriangleIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                {/* Right Side - Maximize/Minimize */}
                                <div className="hidden sm:flex flex-1 items-center gap-3 w-auto justify-end pl-2">
                                    <TranscriptionToggleButton
                                        isOpen={isTranscriptionOpen}
                                        onClick={() => setIsTranscriptionOpen((prev) => !prev)}
                                    />
                                </div>
                            </div>
                        </div>
                        {/* Right column: Transcription Panel - Placed directly in the main content flex container */}
                        {isTranscriptionOpen && (
                            <TranscriptionPanel
                                messages={transcriptionMessages}
                                aiName={aiName}
                                aiSpeaking={isAiSpeaking}
                                userSpeaking={false}
                                aiThinking={isAiThinking}
                                className="border-l border-gray-700 h-full" // Added h-full to ensure it takes full height
                                onClose={() => setIsTranscriptionOpen(false)}
                                aiLogoSrc={aiLogoSrc}
                                colorScheme={isFirstMoveInterview ? 'black' : 'default'}
                                hasFormAssessment={hasFormAssessment}
                                hasEligibilityCheck={hasEligibilityCheck}
                                tipText={tipText}
                            />
                        )}
                    </div>
                </div>
                {/* Settings Modal (Rendered conditionally based on state) */}
                {
                    showSettings && (
                        <SettingsModal
                            isOpen={showSettings}
                            onClose={handleCloseSettings}
                            onSave={handleSettingsSave}
                            initialSelectedCamera={selectedVideoDevice || currentCameraId}
                            initialSelectedMicrophone={selectedAudioDevice || currentMicrophoneId}
                            initialSelectedSpeaker={currentSpeakerId}
                            colorScheme={isFirstMoveInterview ? 'black' : 'default'}
                            microphoneWarning={shouldShowMicWarning}
                        />
                    )
                }

                {/* Issues Dialog - Add it here.
                    `type="live-session"` because this dialog only opens from
                    inside the active interview UI; the waiting-room copy in
                    `custom-voice-interview-v2.tsx` passes "waiting-room". */}
                <IssuesDialog
                    isOpen={isIssuesDialogOpen}
                    onClose={handleIssuesDialogClose}
                    onSubmit={handleIssueSubmit}
                    userId={userId}
                    colorScheme={isFirstMoveInterview ? 'black' : 'default'}
                    type="live-session"
                    selectedAudioDeviceID={selectedAudioDevice || undefined}
                    selectedVideoDeviceID={selectedVideoDevice || undefined}
                />

                <MicDetectionDialog
                    isOpen={showMicDialog}
                    onClose={() => setShowMicDialog(false)}
                    onOpenSettings={handleOpenSettings}
                />
            </div >
        );
    }

    // ** Completed State Screen **
    if (status === "completed") {
        return (
            <FeedbackPopup
                interviewName={aiName}
                assessmentId={assessmentId}
                onComplete={onCloseDialogSuccessRecord}
                interviewStartTime={interviewStartTime}
                userId={userId}
                conversationId={conversationId as string}
                selectedAudioDeviceID={selectedAudioDevice || "default"}
                selectedVideoDeviceID={selectedVideoDevice || "default"}
                interviewLanguage={interviewLanguage}
                hasFormAssessment={hasFormAssessment}
                hasEligibilityCheck={hasEligibilityCheck}
            />
        );
    }

    // ** Error State (If interview fails *after* starting) **
    // This differs from the initial error handled by the parent's waiting room
    if (status === "error") {
        return (
            <div className="w-full h-full min-h-[480px] flex flex-col items-center justify-center text-center p-4 bg-gray-100 dark:bg-gray-900">
                <AlertTriangleIcon className="h-12 w-12 text-red-500 mb-4" />
                <h2 className="text-xl font-semibold text-red-700 dark:text-red-400 mb-2">Interview Error</h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4 max-w-md">
                    {errorMessage || "An unexpected error occurred during the interview."}
                </p>                    <Button
                    onClick={onCloseDialogSuccessRecord}
                    variant="outline"
                    className={isFirstMoveInterview ? "border-black text-black hover:bg-black hover:text-white" : ""}
                >
                    Close
                </Button>
                {/* Optionally add a retry or issue report button here */}
            </div>
        );
    }

    // Fallback Render (Should ideally not be reached if status logic is exhaustive)
    console.warn("VideoContainer reached fallback render with status:", status);
    return (
        <div className="w-full h-full min-h-[480px] flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-6">
            {isTestInterview ? (
                <div className="w-full max-w-md text-center">
                    <div className="inline-flex items-center justify-center p-3 bg-green-100 rounded-full mb-4">
                        <CheckCircleIcon className="h-10 w-10 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2">Interview Complete</h2>
                    {/* <p className="text-gray-600 dark:text-gray-400 mb-6">
                        You've successfully tested your custom interview. No data was saved.
                    </p> */}
                    <div className="space-y-3">                        <Button
                        onClick={onCloseDialogSuccessRecord}
                        className={`w-full ${isFirstMoveInterview ? 'bg-black hover:bg-gray-800' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        Return to Editor
                    </Button>                        <div className={`flex flex-col gap-2 text-sm text-left p-4 ${primaryColorClasses.bgLight} ${primaryColorClasses.bgLightDark} rounded-lg`}>
                            <div className={`flex items-center gap-2 ${primaryColorClasses.text} ${primaryColorClasses.textDark}`}>
                                <InfoIcon className="h-5 w-5" />
                                <h3 className={`font-medium ${primaryColorClasses.text} ${primaryColorClasses.textDark}`}>Testing Tips:</h3>
                            </div>
                            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-1">
                                <li>Try testing with different persona profiles.</li>
                                <li>Ensure all questions are clear and understandable.</li>
                                <li>Verify transitions between sections flow naturally.</li>
                                <li>Check that timing allocations feel appropriate.</li>
                            </ul>
                        </div>
                    </div>
                </div>) : (
                <>
                    <Loader2 className={`h-10 w-10 animate-spin ${primaryColorClasses.loader} mb-4`} />
                    <p className="text-gray-700 dark:text-gray-300">Saving your video...</p>
                </>
            )}
        </div>
    );
}